import {
  type SitemapApiItem,
  type SitemapCMSEnvironment,
  SitemapEnvironment,
  type GroupedSitemapResponse,
  type SitemapArticleObject,
  type SitemapApiResponse,
} from '../types';

type SitemapType = 'html' | 'xml';

export interface FetchSitemapFromApiParams {
  tenantId: string;
  type?: SitemapType;
  environment?: SitemapCMSEnvironment;
  apiBaseUrl: string;
  authToken?: string | null;
  traceHeaders?: Record<string, string> | null;
}

/**
 * Fetches sitemap data from the Content Operations Service API (non-GraphQL).
 *
 * - For `type === 'html'`: returns grouped-by-category object (excluding Uncategorized)
 * - For `type === 'xml'`: returns flat array of ALL entries
 */
export const fetchSitemapFromApi = async ({
  tenantId,
  type = 'xml',
  environment = SitemapEnvironment.PROD,
  apiBaseUrl,
  authToken,
  traceHeaders,
}: FetchSitemapFromApiParams): Promise<
  GroupedSitemapResponse | SitemapArticleObject[]
> => {
  if (!tenantId || !apiBaseUrl || !environment) {
    throw new Error(
      `Missing required parameters: tenantId: ${tenantId || 'null'}, apiBaseUrl: ${apiBaseUrl || 'null'}, environment: ${environment || 'null'}`,
    );
  }

  const endpoint = `${apiBaseUrl}/v1/sitemap/${encodeURIComponent(tenantId)}/results`;
  const url = new URL(endpoint);
  url.searchParams.append('environment', environment);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  if (traceHeaders) {
    for (const [key, value] of Object.entries(traceHeaders)) {
      if (value) headers[key] = value;
    }
  }

  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
      signal: AbortSignal.timeout(30000),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(
        `Sitemap API request failed: ${response.status} ${response.statusText}${errorText ? ` with ${errorText}` : ''}`,
      );
    }

    const data: SitemapApiResponse = await response.json();

    if (!data?.sitemap || !Array.isArray(data.sitemap)) {
      // Keep this as console.error to match middleware behavior.
      console.error('[wordpress-lib] Invalid sitemap response format:', data);
      throw new Error('Invalid sitemap response format');
    }

    // Transform API items to our internal structure.
    const transformedPages: SitemapArticleObject[] = data.sitemap.map(
      (item) => {
        const apiItem = item as SitemapApiItem;
        return {
          uri: apiItem.url,
          title: apiItem.title,
          lastModified: apiItem.lastModified || '',
          category: apiItem.category || null,
        };
      },
    );

    if (type === 'html') {
      // Step 1: unique categories excluding Uncategorized
      const categories = new Set<string>();
      for (const node of transformedPages) {
        if (node.category && node.category.toLowerCase() !== 'uncategorized') {
          categories.add(node.category);
        }
      }

      // Step 2: filter non-empty categories
      const filteredPages = transformedPages.filter(
        (node) =>
          node.category !== null &&
          node.category !== undefined &&
          node.category.length > 0,
      );

      // Step 3: group by category, excluding Uncategorized via the set
      const groupedPages = filteredPages.reduce((acc, node) => {
        if (node.category && categories.has(node.category)) {
          if (!acc[node.category]) acc[node.category] = [];
          acc[node.category].push({
            uri: node.uri,
            title: node.title,
            lastModified: node.lastModified,
          });
        }
        return acc;
      }, {} as GroupedSitemapResponse);

      // Step 4: sort by title within each category
      for (const category of Object.keys(groupedPages)) {
        groupedPages[category].sort((a, b) => {
          if (a.title < b.title) return -1;
          if (a.title > b.title) return 1;
          return 0;
        });
      }

      // Step 5: sort category keys
      const sortedGroupedPages: GroupedSitemapResponse = {};
      for (const key of Object.keys(groupedPages).sort()) {
        sortedGroupedPages[key] = groupedPages[key];
      }

      return sortedGroupedPages;
    }

    // XML: return flat array of ALL entries
    return transformedPages;
  } catch (error) {
    if (error instanceof Error) {
      console.error(
        '[wordpress-lib] Error occurred while fetching sitemap:',
        error.message,
      );

      // Node/undici uses DOMException AbortError; middleware checked Error.name === AbortError.
      if (error.name === 'AbortError') {
        throw new Error('Sitemap API request timeout');
      }

      throw new Error(`Failed to fetch sitemap from API: ${error.message}`);
    }

    console.error(
      '[wordpress-lib] Unknown error occurred while fetching sitemap:',
      error,
    );
    throw new Error('Unknown error occurred while fetching sitemap');
  }
};

export default fetchSitemapFromApi;
